import {
  DEFAULT_DELEGATION_TIERS,
  DELEGABLE_TIERS,
  validateDelegationPolicy,
  type ActorDTO,
  type DecisionDTO,
  type DelegationBlock,
  type DelegationPolicy,
  type Tier,
} from "@roomate/core";

/**
 * Draft form state for the profile's Reviews panel. `tiers`/`areasText`/`spotCheck` are kept even
 * while `enabled` is false, so flipping the switch back on restores what the person had, rather
 * than resetting to the defaults.
 */
export type DelegationDraft = {
  enabled: boolean;
  agentId: string;
  tiers: Tier[];
  areasText: string;
  spotCheck: boolean;
};

export function splitAreas(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function draftFromPolicy(policy: DelegationPolicy | null): DelegationDraft {
  // A fresh array, not the shared module-level default, so no draft ever aliases the constant
  // (the tier checkboxes replace `draft.tiers` rather than mutate it, but nothing should rely on that).
  if (!policy) return { enabled: false, agentId: "", tiers: [...DEFAULT_DELEGATION_TIERS], areasText: "", spotCheck: false };
  return {
    enabled: true,
    agentId: policy.agentId,
    tiers: policy.tiers,
    areasText: policy.areas.join(", "),
    spotCheck: policy.kinds.includes("spot_check"),
  };
}

/** `null` when the switch is off — the shape `PATCH /api/actors/:id` expects to turn delegation off. */
export function policyFromDraft(draft: DelegationDraft): DelegationPolicy | null {
  if (!draft.enabled) return null;
  return {
    agentId: draft.agentId,
    tiers: draft.tiers,
    areas: splitAreas(draft.areasText),
    kinds: draft.spotCheck ? ["review_change", "spot_check"] : ["review_change"],
  };
}

/**
 * Inline validation, reusing `validateDelegationPolicy` from `@roomate/core` rather than a second
 * copy of the rules — the same function the server calls, so a message here and a 400 from the
 * server map to the same field via `fieldForProblem`. Empty array = fine to save.
 */
export function validateDraft(draft: DelegationDraft, context: { actor: ActorDTO; agents: ActorDTO[] }): string[] {
  const policy = policyFromDraft(draft);
  if (!policy) return [];
  const agent = context.agents.find((a) => a.id === policy.agentId) ?? null;
  return validateDelegationPolicy(policy, { actor: context.actor, agent });
}

// No "areas" field: validateDelegationPolicy has no rule about areas, so nothing could land there.
export type DelegationField = "agentId" | "tiers" | "general";

/**
 * Maps one of `validateDelegationPolicy`'s problem sentences (client-side, or a server 400 that
 * joins the same sentences with "; ") to the field it names, so the error renders next to the
 * control it's about rather than as a banner (SPEC: a 400 is shown against the field it names).
 */
export function fieldForProblem(problem: string): DelegationField {
  if (problem.includes("agent")) return "agentId";
  if (problem.includes("tiers") || problem.includes("critical")) return "tiers";
  return "general";
}

export function groupProblems(problems: string[]): Record<DelegationField, string[]> {
  const grouped: Record<DelegationField, string[]> = { agentId: [], tiers: [], general: [] };
  for (const p of problems) grouped[fieldForProblem(p)].push(p);
  return grouped;
}

/** Splits a server 400's message back into the same sentences `validateDelegationPolicy` joined with "; ". */
export function splitServerProblems(message: string): string[] {
  return message
    .split("; ")
    .map((s) => s.trim())
    .filter(Boolean);
}

export { DELEGABLE_TIERS };

/**
 * Presentational copy for each `DelegationBlock`, for the Bridge's "why a person's click is
 * needed" line. A `Record` over the whole union (not a hand-picked subset) so a new block added to
 * `@roomate/core` fails to compile here until this copy is written for it — the same exhaustiveness
 * trick `packages/core/src/delegation.ts` uses for `HARD_RULE_EFFECTS`.
 */
export const DELEGATE_BLOCK_WHY: Record<DelegationBlock, string> = {
  no_policy: "no delegation policy is set for this person",
  kind_not_delegated: "this kind of review isn't delegated",
  agent_inactive: "the delegate agent is inactive",
  agent_not_owned: "the delegate agent is not owned by this person",
  builder_is_delegate: "an agent never reviews a change it built",
  tier_critical: "critical risk always needs two human reviews",
  tier_not_in_policy: "this risk tier is outside your delegation policy",
  area_not_in_policy: "this change's areas are outside your delegation policy",
  hard_rule: "a project rule requires a human reviewer here",
  verdict_inconclusive: "the verification verdict was inconclusive",
  tests_weakened: "the submitted evidence weakened tests",
  // Verbatim from packages/core/src/delegation.ts BLOCK_WHY (not exported), so web and server say the same thing.
  shares_owner_with_builder: "the delegate shares an owner with the agent that built this high-risk change",
};

export type DelegateFinding = { severity: "critical" | "important" | "minor"; text: string };

export type DelegateBlockView = {
  /** The decision's `delegateId` — the agent that reviewed it. */
  agentId: string;
  /** The decision's `assigneeId` — the person the agent reviewed on behalf of. */
  personId: string | null;
  recommendedOptionId: string;
  rationale: string;
  critical: DelegateFinding[];
  important: DelegateFinding[];
  minorCount: number;
  /** One sentence: why a person's click is still needed. */
  whyLabel: string;
};

/**
 * The Bridge's one-click-confirm block, derived from the decision alone — no actor lookup here, so
 * this stays a pure, unit-testable view model. `null` for a decision with no delegation, or one
 * whose agent hasn't reported back yet (`delegateVerdict` unset): nothing new renders then, which
 * is what keeps an undelegated decision's Bridge card byte-identical to today.
 */
export function delegateBlockView(d: Pick<DecisionDTO, "delegateId" | "delegateState" | "delegateVerdict" | "assigneeId">): DelegateBlockView | null {
  // `delegateState` and `delegateVerdict` are set atomically server-side, so this guard is
  // belt-and-suspenders rather than load-bearing — but it means the view model doesn't lean on
  // that server invariant holding forever, e.g. if a 'failed' decision ever carried a stale verdict.
  if (!d.delegateId || d.delegateState !== "ran" || !d.delegateVerdict) return null;
  const v = d.delegateVerdict;
  const bySeverity = (s: DelegateFinding["severity"]) => v.findings.filter((f) => f.severity === s);
  return {
    agentId: d.delegateId,
    personId: d.assigneeId,
    recommendedOptionId: v.optionId,
    rationale: v.rationale,
    critical: bySeverity("critical"),
    important: bySeverity("important"),
    minorCount: bySeverity("minor").length,
    // An open decision carrying a verdict with no block shouldn't exist (an in-policy verdict
    // answers it server-side), so don't invent a reason — just say what is true on screen.
    whyLabel: v.blocks[0] ? DELEGATE_BLOCK_WHY[v.blocks[0]] : "a person's answer is still needed",
  };
}
